import { useEffect, useMemo, useState } from 'react'
import { PageFrame } from '@/components/layout/PageFrame'
import { PageHeader } from '@/components/layout/PageHeader'
import { History, Plug, ShieldCheck, Sparkles } from 'lucide-react'
import { useQueryClient } from '@tanstack/react-query'
import { Alert, AlertDescription, Tabs, TabsList, TabsTrigger, TabsContent, useToast, Spinner } from '@/components/ui'
import { useAdapters, useCapabilities } from '@/hooks'
import { useIntegrationCatalog } from '@/hooks/useIntegrations'
import type { Capability } from '@/types/toolFramework'
import type { IntegrationCatalogEntry } from '@/types/integrations'
import { CapabilityList } from './CapabilityList'
import { AdapterCard } from './AdapterCard'
import { AuditLog } from './AuditLog'
import { ApprovalQueue } from './ApprovalQueue'

interface AdaptersTabProps {
  onConnectComplete: (result: { adapterId: string; ok: boolean; error?: string }) => void
}

function AdaptersTab({ onConnectComplete }: AdaptersTabProps) {
  const { data: adapters, isLoading: adaptersLoading, error: adaptersError } = useAdapters()
  const { data: caps } = useCapabilities()
  const { data: catalog } = useIntegrationCatalog()
  const capabilities: Capability[] = caps ?? []
  const byId: Record<string, IntegrationCatalogEntry> = useMemo(
    () => Object.fromEntries((catalog?.integrations ?? []).map((e) => [e.adapter_id, e])),
    [catalog],
  )

  if (adaptersLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    )
  }

  if (adaptersError) {
    return (
      <Alert variant="destructive">
        <AlertDescription>
          {adaptersError instanceof Error ? adaptersError.message : 'Failed to load adapters'}
        </AlertDescription>
      </Alert>
    )
  }

  if (!adapters || adapters.length === 0) {
    return (
      <div className="text-sm text-muted-foreground py-12 text-center">
        No adapters registered yet.
      </div>
    )
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {adapters.map((adapter) => (
        <AdapterCard
          key={adapter.id}
          adapter={adapter}
          capabilities={capabilities}
          catalogEntry={byId[adapter.id]}
          onConnectComplete={onConnectComplete}
        />
      ))}
    </div>
  )
}

export function ToolLibraryPage() {
  const [activeTab, setActiveTab] = useState('capabilities')
  const qc = useQueryClient()
  const { addToast } = useToast()

  useEffect(() => {
    if (!window.opener || window.opener === window) return
    const params = new URLSearchParams(window.location.search)
    const connected = params.get('connected')
    const oauthError = params.get('oauth_error')

    if (connected !== null || oauthError !== null) {
      window.opener.postMessage(
        {
          type: 'adapter-oauth-callback',
          adapterId: connected ?? '',
          ok: connected !== null && oauthError === null,
          error: oauthError ?? undefined,
        },
        window.location.origin,
      )
      window.close()
    }
  }, [])

  function handleConnectComplete(result: { adapterId: string; ok: boolean; error?: string }) {
    if (result.ok) {
      qc.invalidateQueries({ queryKey: ['adapters'] })
      qc.invalidateQueries({ queryKey: ['integration-catalog'] })
      addToast({ type: 'success', title: `Adapter ${result.adapterId} connected` })
    } else if (result.error && result.error !== 'cancelled') {
      addToast({ type: 'error', title: 'Connection failed', description: result.error })
    }
  }

  return (
    <PageFrame width="list" className="space-y-4">
      <PageHeader
        title="Tool Library"
        subtitle="Manage capabilities, adapters, approval queues, and audit logs for the tool framework."
      />

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="capabilities" className="flex items-center gap-1.5">
            <Sparkles className="w-4 h-4" />
            Capabilities
          </TabsTrigger>
          <TabsTrigger value="adapters" className="flex items-center gap-1.5">
            <Plug className="w-4 h-4" />
            Adapters
          </TabsTrigger>
          <TabsTrigger value="audit" className="flex items-center gap-1.5">
            <History className="w-4 h-4" />
            Audit
          </TabsTrigger>
          <TabsTrigger value="approvals" className="flex items-center gap-1.5">
            <ShieldCheck className="w-4 h-4" />
            Approvals
          </TabsTrigger>
        </TabsList>

        <TabsContent value="capabilities">
          <CapabilityList />
        </TabsContent>
        <TabsContent value="adapters">
          <AdaptersTab onConnectComplete={handleConnectComplete} />
        </TabsContent>
        <TabsContent value="audit"><AuditLog /></TabsContent>
        <TabsContent value="approvals"><ApprovalQueue /></TabsContent>
      </Tabs>
    </PageFrame>
  )
}
